import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { CheckCircle2, XCircle, CircleDashed, Printer, Table2, Loader2, Sparkles, Trophy } from "lucide-react";

const fmt = (n) => Number(n || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const CATEGORY_STYLE = {
  "Económico":      { bg: "bg-[#007934]", text: "text-white" },
  "Pliego":         { bg: "bg-blue-700",  text: "text-white" },
  "Campo":          { bg: "bg-amber-700", text: "text-white" },
  "Certificaciones":{ bg: "bg-purple-700",text: "text-white" },
  "Integraciones":  { bg: "bg-teal-700",  text: "text-white" },
  "Contrato":       { bg: "bg-gray-700",  text: "text-white" },
};

// Badge de puntuación Pando — color según rango
function AIScoreBadge({ score, isWinner }) {
  if (score == null) return null;
  const n = Number(score);
  let cls = "bg-gray-100 text-gray-600";
  if (n >= 8) cls = "bg-green-100 text-green-800 border-green-300";
  else if (n >= 5) cls = "bg-amber-100 text-amber-800 border-amber-300";
  else cls = "bg-red-100 text-red-700 border-red-300";
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded border ${cls} ${isWinner ? "ring-1 ring-[#007934] ring-offset-1" : ""}`}>
      {isWinner && <Trophy size={9} />}
      Pando {n}/10
    </span>
  );
}

// Renderiza cada celda según el tipo de dato
function CellValue({ type, unit, value }) {
  if (type === "currency") {
    return <span className="font-semibold text-gray-900">{fmt(value)} {unit || "€"}</span>;
  }
  // feature: yes/no/partial o texto literal
  if (value === "yes") {
    return (
      <span className="inline-flex items-center gap-1.5 text-green-700">
        <CheckCircle2 size={16} className="flex-shrink-0" />
        <span className="text-xs font-medium">Sí</span>
      </span>
    );
  }
  if (value === "no") {
    return (
      <span className="inline-flex items-center gap-1.5 text-red-600">
        <XCircle size={16} className="flex-shrink-0" />
        <span className="text-xs font-medium">No</span>
      </span>
    );
  }
  if (value === "partial") {
    return (
      <span className="inline-flex items-center gap-1.5 text-amber-700">
        <CircleDashed size={16} className="flex-shrink-0" />
        <span className="text-xs font-medium">Parcial</span>
      </span>
    );
  }
  return <span className="text-xs text-gray-800 font-medium">{String(value)}</span>;
}

export function ScenarioComparisonMatrix({ offerId, activeScenario }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await axios.get(`${API}/offers/${offerId}/scenarios-comparison`);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "No se pudo cargar el comparador");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [offerId, activeScenario]);

  const handlePrint = () => {
    const printWindow = window.open("", "_blank", "width=1024,height=768");
    if (!printWindow || !data) return;
    const rowsHtml = (data.rows || [])
      .map((r) => {
        const cells = (data.headers || [])
          .map((h) => {
            const v = r.values[h.key];
            let display = v;
            if (r.type === "currency") display = `${fmt(v)} ${r.unit || "€"}`;
            else if (v === "yes") display = "✓ Sí";
            else if (v === "no") display = "✗ No";
            else if (v === "partial") display = "◐ Parcial";
            const aiScore = r.ai?.scores?.[h.key];
            const winner = r.ai?.winner === h.key;
            const aiBadge = aiScore != null
              ? `<div style="margin-top:3px;font-size:9px;font-weight:700;color:${aiScore>=8?'#166534':aiScore>=5?'#92400e':'#b91c1c'}">Pando ${aiScore}/10${winner?' 🏆':''}</div>`
              : "";
            return `<td style="padding:8px 10px;border:1px solid #ddd;font-size:11px;vertical-align:top">${display}${aiBadge}</td>`;
          })
          .join("");
        const aiCell = data.ai_available
          ? `<td style="padding:8px 10px;border:1px solid #ddd;font-size:10px;background:#faf5ff;vertical-align:top">${
              r.ai
                ? `<div style="font-weight:700;color:#6b21a8;margin-bottom:2px">🏆 ${r.ai.winner || "—"}</div><div style="color:#374151">${r.ai.justification || ""}</div>`
                : "—"
            }</td>`
          : "";
        return `<tr>
          <td style="padding:8px 10px;border:1px solid #ddd;background:#f9fafb;font-size:10px;font-weight:700;color:#6b7280;text-transform:uppercase;vertical-align:top">${r.category}</td>
          <td style="padding:8px 10px;border:1px solid #ddd;font-size:11px;vertical-align:top">${r.label}</td>
          ${cells}
          ${aiCell}
        </tr>`;
      })
      .join("");
    const headersHtml = (data.headers || [])
      .map((h) => `<th style="padding:10px;background:#007934;color:white;border:1px solid #007934;font-size:11px;text-align:left">${h.label}</th>`)
      .join("");
    const aiHeader = data.ai_available
      ? `<th style="padding:10px;background:#6b21a8;color:white;border:1px solid #6b21a8;font-size:11px;text-align:left;width:220px">Veredicto Pando · Claude</th>`
      : "";
    const html = `<!doctype html><html><head><meta charset="utf-8">
      <title>Comparador de Escenarios · Valoriza</title>
      <style>body{font-family:Arial,sans-serif;padding:24px;color:#111}h1{font-size:18px;margin:0 0 4px;color:#007934}p.meta{font-size:11px;color:#6b7280;margin:0 0 16px}table{border-collapse:collapse;width:100%}</style>
      </head><body>
      <h1>Comparador de Escenarios Económicos — Oferta TIC</h1>
      <p class="meta">Generado: ${new Date(data.generated_at).toLocaleString("es-ES")} · Escenario activo: ${data.active_scenario || "—"}${data.ai_available ? " · Evaluación Pando: Claude Sonnet 4.6" : ""}</p>
      <table>
        <thead><tr>
          <th style="padding:10px;background:#111;color:white;border:1px solid #111;font-size:10px;text-align:left;width:110px">Categoría</th>
          <th style="padding:10px;background:#111;color:white;border:1px solid #111;font-size:11px;text-align:left">Criterio</th>
          ${headersHtml}
          ${aiHeader}
        </tr></thead>
        <tbody>${rowsHtml}</tbody>
      </table>
      <p style="margin-top:18px;font-size:10px;color:#6b7280">Valoriza Servicios Medioambientales · Plataformas Corporativas · Documento confidencial.</p>
      <script>window.onload=()=>{window.print();}</script>
      </body></html>`;
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 p-6 flex items-center gap-3 text-gray-500 text-sm">
        <Loader2 size={16} className="animate-spin" />
        Cargando comparador…
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 p-4 text-xs text-red-600">
        {error}
      </div>
    );
  }

  if (!data || !data.rows?.length) return null;

  // Agrupar filas por categoría
  const groups = data.rows.reduce((acc, row) => {
    const cat = row.category || "Otros";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(row);
    return acc;
  }, {});

  return (
    <div data-testid="scenario-comparison-matrix" className="bg-white rounded-xl border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-100 bg-gray-50">
        <div className="flex items-center gap-2">
          <Table2 size={16} className="text-[#007934]" />
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Comparador de escenarios</p>
            <p className="text-sm font-semibold text-gray-900">DISTROMEL · CHIP2CHIP · Híbrido — matriz de decisión</p>
          </div>
          {data.ai_available && (
            <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 border border-purple-300">
              <Sparkles size={10} /> Puntuación Pando · Claude
            </span>
          )}
        </div>
        <button
          data-testid="print-comparison-btn"
          onClick={handlePrint}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs font-medium text-gray-700 hover:border-[#007934] hover:text-[#007934] transition-all"
        >
          <Printer size={13} />
          Imprimir anexo comité
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-900 text-white">
              <th className="px-4 py-3 text-[10px] uppercase tracking-wider text-left w-32">Categoría</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-wider text-left">Criterio</th>
              {data.headers.map((h) => (
                <th
                  key={h.key}
                  className={`px-4 py-3 text-left text-xs font-semibold ${
                    h.key === data.active_scenario ? "bg-[#007934]" : "bg-gray-800"
                  }`}
                >
                  <div className="flex items-center gap-1.5">
                    {h.key === data.active_scenario && <CheckCircle2 size={13} />}
                    <span>{h.label}</span>
                  </div>
                  {h.provider && (
                    <div className="text-[10px] text-gray-300 font-normal mt-0.5 truncate max-w-[180px]">
                      {h.provider}
                    </div>
                  )}
                </th>
              ))}
              {data.ai_available && (
                <th className="px-4 py-3 text-left text-xs font-semibold bg-purple-700 w-64">
                  <div className="flex items-center gap-1.5">
                    <Sparkles size={12} />
                    <span>Veredicto Pando</span>
                  </div>
                  <div className="text-[10px] text-purple-100 font-normal mt-0.5">Claude Sonnet 4.6</div>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {Object.entries(groups).map(([category, rows]) => (
              <React.Fragment key={category}>
                {rows.map((row, idx) => {
                  const style = CATEGORY_STYLE[category] || { bg: "bg-gray-500", text: "text-white" };
                  const aiWinner = row.ai?.winner;
                  const aiScores = row.ai?.scores || {};
                  return (
                    <tr key={`${category}-${idx}`} className="border-t border-gray-100 hover:bg-gray-50/50 align-top">
                      {idx === 0 ? (
                        <td
                          rowSpan={rows.length}
                          className={`px-3 py-3 align-top ${style.bg} ${style.text} font-semibold text-[10px] uppercase tracking-wider`}
                        >
                          {category}
                        </td>
                      ) : null}
                      <td className="px-4 py-2.5 text-xs text-gray-700">{row.label}</td>
                      {data.headers.map((h) => (
                        <td
                          key={h.key}
                          className={`px-4 py-2.5 ${h.key === data.active_scenario ? "bg-green-50/60" : ""}`}
                        >
                          <div className="flex flex-col gap-1">
                            <CellValue type={row.type} unit={row.unit} value={row.values[h.key]} />
                            {aiScores[h.key] != null && (
                              <AIScoreBadge score={aiScores[h.key]} isWinner={aiWinner === h.key} />
                            )}
                          </div>
                        </td>
                      ))}
                      {data.ai_available && (
                        <td className="px-4 py-2.5 bg-purple-50/40">
                          {row.ai ? (
                            <div className="flex flex-col gap-1">
                              {aiWinner && (
                                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-purple-800">
                                  <Trophy size={10} />
                                  {(data.headers.find((h) => h.key === aiWinner)?.label || aiWinner).split(" ")[0]}
                                </span>
                              )}
                              <span className="text-[11px] text-gray-700 leading-snug">{row.ai.justification}</span>
                            </div>
                          ) : (
                            <span className="text-[10px] text-gray-400">—</span>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="px-5 py-2.5 border-t border-gray-100 bg-gray-50 text-[10px] text-gray-500">
        TCO calculado con IPC 2 %/año · Escenario activo marcado en verde · Generado{" "}
        {data.generated_at ? new Date(data.generated_at).toLocaleString("es-ES") : "—"}
        {data.ai_available && data.ai_scores_generated_at && (
          <>
            {" · "}
            <span className="text-purple-700">Pando evaluación: {new Date(data.ai_scores_generated_at).toLocaleString("es-ES")}</span>
          </>
        )}
      </div>
    </div>
  );
}
